import {
  AppFactory,
  AppPluginInterface,
  arrayToTree,
  NavigationGroupInterface,
} from "@opendash/core";
import { ObjectWithChildren } from "@opendash/core/dist/helper/arrayToTree";
import { $parse } from "@opendash/plugin-parse";
import { Assets } from "@opendash/plugin-parse/dist/types-generated";
import Parse from "parse";
import { Category, Document } from "./js/parsegenerated";
import ContentField from "./pages/ContentField";
import DokuPage from "./pages/DokuPage";

export class DocumentationPlugin implements AppPluginInterface {
  name: string = "DocumentationPlugin";

  constructor() {}

  async onFactory(factory: AppFactory) {
    factory.registerRoute({
      path: "/documentation/overview/:id",
      component: async () => ({ default: DokuPage }),

      headerSourcePicker: false,
      permission: "documentation:can-see-app",
    });

    //Parse-daten abfragen für Doku
    async function fetchDocumentDetails(): Promise<Document[]> {
      const query = new Parse.Query(Document).include("category");
      query.ascending("order");
      try {
        const results = await query.find();

        return results;
      } catch (error) {
        console.error("Fehler beim Abrufen der Dokumente:", error);
        return [];
      }
    }
    //_________________________________________________________________________________________________________
    //Kategorie Group

    function categoryToNavigationGroup(
      cat: ObjectWithChildren<Category>
    ): NavigationGroupInterface {
      const subgroups = cat.children
        ? cat.children?.map((obj) => categoryToNavigationGroup(obj))
        : [];

      return {
        id: cat.id,
        label: cat.get("name") || "Ohne Titel",
        order: cat.get("order"),
        icon: cat.get("icon"),
        subgroups: subgroups,
      };
    }

    //Parse-daten abfragen für Kategorie
    async function fetchCategories(): Promise<Category[]> {
      const query = new Parse.Query(Category).include("parent");
      try {
        const results = await query.find();
        const tree = arrayToTree(
          results,
          (obj) => obj.get("parent")?.id,
          (obj) => obj.id
        );

        const groups = tree.map((obj) => categoryToNavigationGroup(obj));
        // groups.forEach((group) => );

        for (const group of groups) {
          factory.registerStaticNavigationGroup(group);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Kategorien:", error);
        return [];
      }
    }
    await fetchCategories();

    //_____________________________________________________________________________
    // Navigation für Dokumentation Frontpage:
    factory.registerStaticNavigationItem({
      id: "documentation/home",
      place: "frontpage",
      group: "documentation",
      order: 15,
      color: "#676767",

      label: "Documentation",
      icon: "fa:book",

      link: "/documentation/overview/*",

      routeCondition: "**",
      activeCondition: "/documentation",
      permission: "documentation:can-see-app",
    });

    factory.registerStaticNavigationGroup({
      id: "documentation_nav_group",
      label: "documentation:nav.label",
      icon: "fa:book",
      order: 40,
    });

    //__________________________________________________________________________________________

    // Funktion zum Erstellen der Navigationsitems
    async function createDynamicNavigationItems() {
      const documents = await fetchDocumentDetails(); // Dokumente abrufen

      // Iteriere über alle Dokumente und registriere ein Navigationsitem für jedes
      documents.forEach((document, index) => {
        factory.registerStaticNavigationItem({
          id: `documentation_nav_item_${index}`, // Eindeutige ID für jedes Dokument
          group: document.get("category")?.id,
          place: "sidebar",
          order: 0 + index, // Optional: bestimme die Reihenfolge
          label: document.title, // Titel des Dokuments als Label
          icon: document.get("icon"),
          link: `/documentation/overview/${document.id}`, // Dynamische URL für jedes Dokument
          event: "string",
          routeCondition: "/documentation/**",
          activeCondition: `/documentation/overview/${document.id}`,
          permission: "documentation:can-see-app",
        });
      });
    }

    // Navigationsitems erstellen
    await createDynamicNavigationItems();
    //___________________________________________________________________________________________________________________________________
    // Translations:
    factory.registerTranslationResolver(
      "en",
      "documentation",
      async () => await import("./translations/en.json")
    );

    factory.registerTranslationResolver(
      "de",
      "documentation",
      async () => await import("./translations/de.json")
    );
    factory.registerTranslationResolver(
      "nl",
      "documentation",
      async () => await import("./translations/nl.json")
    );
    //_____________________________________________________________________________________________________________________________________________________
    //Für Category Admin (um Kategorien hinzuzufügen)

    factory.registerStaticNavigationGroup({
      id: "documentation/admin/OD3_Documentation_Category",
      label: "documentation:classes.OD3_Documentation_Category.label",
      icon: "fa:book",
      order: 50,
    });

    factory.registerStaticNavigationItem({
      id: "documentation/admin/OD3_Documentation_Category_item",
      group: "documentation/admin/OD3_Documentation_Category",
      place: "sidebar",
      order: 110,

      label: "documentation:classes.OD3_Documentation_Category.label_plural",
      icon: "fa:th-list",
      link: "/admin/parse/OD3_Documentation_Category",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Documentation_Category",
      permission: "documentation:can-see-app",
    });

    //Kategorien hinzufügen
    $parse.ui.setDefaultView("OD3_Documentation_Category", {
      type: "table",
    });
    $parse.ui.setClassConfig<Category>({
      beforeCreate: () => {
        const cat = new Category();
        cat.set("user", Parse.User.current());
        cat.set("tenant", Parse.User.current().get("tenant"));
        return cat;
      },

      className: "OD3_Documentation_Category",
      disableACLEditing: false,

      titleFields: ["name"],
      displayFields: ["name", "parent", "order", "user", "icon"],
      createFields: ["name", "parent", "order", "icon"],
      editFields: ["name", "parent", "order", "icon"],
      translationPrefix: "documentation:classes.",

      defaultACL: () => {
        //(Felix)
        if (!$parse.user.id()) {
          return {};
        }

        return {
          [$parse.user.id()]: {
            read: true,
            write: true,
          },
        };
      },
    });
    //____________________________________________________________________________________________________________________________________________
    // Dokumentation in der Bib

    factory.registerStaticNavigationItem({
      id: "documentation/admin/OD3_Documentation_Document_item",
      group: "documentation/admin/OD3_Documentation_Category",
      place: "sidebar",
      order: 115,

      label: "documentation:classes.OD3_Documentation_Document.label_plural",
      icon: "fa:th-list",
      link: "/admin/parse/OD3_Documentation_Document",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Documentation_Document",
      permission: "documentation:can-see-app",
    });

    //Dokumente hinzufügen
    $parse.ui.setDefaultView("OD3_Documentation_Document", {
      type: "table",
    });
    $parse.ui.setClassConfig<Document>({
      beforeSave(object) {
        object.set("user", Parse.User.current());
        object.set("tenant", Parse.User.current().get("tenant"));
      },

      className: "OD3_Documentation_Document",
      disableACLEditing: false,

      titleFields: ["title"],
      displayFields: ["title", "content", "category", "user", "order", "icon"],
      createFields: ["title", "content", "category", "order", "icon"],
      editFields: ["title", "content", "category", "order", "icon"],
      translationPrefix: "documentation:classes.",
      customFields: {
        content: ContentField, //TEST!!!
      },
      defaultACL: () => {
        //(Felix)
        if (!$parse.user.id()) {
          return {};
        }

        return {
          [$parse.user.id()]: {
            read: true,
            write: true,
          },
        };
      },
    });

    // Files ____________________________________________________________________________________________

    //files Sidebar anzeigen im Admin

    factory.registerStaticNavigationItem({
      id: "documentation/admin/OD3_Assets_item",
      group: "documentation/admin/OD3_Documentation_Category",
      place: "sidebar",
      order: 118,

      label: "documentation:classes.OD3_Assets.label_plural",
      icon: "fa:th-list",
      link: "/admin/parse/OD3_Assets",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Assets",
      permission: "documentation:can-see-app",
    });

    // Files hinzufügen

    $parse.ui.setDefaultView("OD3_Assets", {
      type: "table",
    });
    $parse.ui.setClassConfig<Assets>({
      beforeSave(object) {
        object.set("user", Parse.User.current());
        object.set("tenant", Parse.User.current().get("tenant"));
        const file = object.get("file");
        const allowedFile = (name: string, dataForm: string[]) => {
          name = file.name().split(".").pop().toLowerCase();
          if (!dataForm.includes(name)) {
            throw new Error("not allowed");
          }
        };
        allowedFile(file.name(), ["png", "jpg", "jpeg"]);
      },

      className: "OD3_Assets",
      disableACLEditing: false,

      titleFields: ["title"],
      displayFields: ["description", "file", "meta", "user"],
      createFields: ["description", "file", "meta"],
      editFields: ["description", "file", "meta"],
      translationPrefix: "documentation:classes.",
      customFields: {
        content: ContentField, //TEST!!!!
      },

      defaultACL: () => {
        //(Felix)
        if (!$parse.user.id()) {
          return {};
        }

        return {
          [$parse.user.id()]: {
            read: true,
            write: true,
          },
        };
      },
    });
  }
}
